import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Grid,
  alpha,
  Paper,
  Chip,
  Dialog,
  Divider,
  LinearProgress,
  Alert,
  Backdrop,
  CircularProgress,
  InputAdornment,
  Tooltip,
  useTheme,
  Autocomplete,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Avatar,
  TableFooter,
} from "@mui/material";
import {
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  Whatshot as WhatshotIcon,
  LocalAtm as LocalAtmIcon,
  Numbers as NumbersIcon,
  Diamond as DiamondIcon,
  Scale as ScaleIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DiscountIcon from "@mui/icons-material/Discount";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { DataContext } from "../../context/DataContext";

// Skeleton Component
const SupplierTransactionHistorySkeleton = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const SkeletonCard = ({ children, height = "auto", sx = {} }) => (
    <Card
      sx={{
        borderRadius: "16px",
        background: isDark
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
        backdropFilter: "blur(10px)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
        ...sx,
      }}
    >
      <CardContent sx={{ p: 3 }}>{children}</CardContent>
    </Card>
  );

  const SkeletonLine = ({ width = "100%", height = 20, variant = "text" }) => (
    <Skeleton
      variant={variant}
      width={width}
      height={height}
      animation="wave"
      sx={{
        bgcolor: alpha(isDark ? "#ffffff" : "#000000", 0.08),
        borderRadius: variant === "rounded" ? 2 : 1,
      }}
    />
  );

  const SkeletonStatCard = () => (
    <Card
      sx={{
        borderRadius: "16px",
        backgroundColor: "transparent",
        backdropFilter: "blur(10px)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.08)",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <SkeletonLine
            variant="circular"
            width={40}
            height={40}
            sx={{ mr: 2 }}
          />
          <SkeletonLine width={120} height={16} />
        </Box>
        <SkeletonLine width={100} height={32} sx={{ mb: 1 }} />
        <SkeletonLine width={80} height={14} />
      </CardContent>
    </Card>
  );

  const SkeletonTableRow = () => (
    <TableRow>
      <TableCell>
        <SkeletonLine width={30} height={20} />
      </TableCell>
      <TableCell>
        <SkeletonLine width={80} height={20} />
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SkeletonLine variant="circular" width={40} height={40} />
          <Box>
            <SkeletonLine width={120} height={18} sx={{ mb: 0.5 }} />
            <SkeletonLine width={100} height={14} />
          </Box>
        </Box>
      </TableCell>
      <TableCell align="center">
        <SkeletonLine width={70} height={28} variant="rounded" />
      </TableCell>
      <TableCell align="right">
        <SkeletonLine width={80} height={20} />
      </TableCell>
      <TableCell align="right">
        <SkeletonLine width={80} height={20} />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
          <SkeletonLine variant="circular" width={32} height={32} />
          <SkeletonLine variant="rounded" width={70} height={28} />
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minHeight: "100vh", py: 4 }}>
        <Container maxWidth="xl">
          {/* Header Skeleton */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 3,
            }}
          >
            <Box>
              <SkeletonLine width={350} height={48} sx={{ mb: 1 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SkeletonLine variant="circular" width={20} height={20} />
                <SkeletonLine width={250} height={20} />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <SkeletonLine variant="rounded" width={100} height={40} />
              <SkeletonLine variant="rounded" width={120} height={40} />
              <SkeletonLine variant="rounded" width={120} height={40} />
              <SkeletonLine variant="rounded" width={100} height={40} />
            </Box>
          </Box>

          {/* Stats Cards Grid */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <SkeletonStatCard />
              </Grid>
            ))}
          </Grid>

          {/* Filters Card Skeleton */}
          <SkeletonCard sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <SkeletonLine variant="rounded" height={50} width="250px" />
              </Grid>
              <Grid item xs={12} md={4}>
                <SkeletonLine variant="rounded" height={50} width="250px" />
              </Grid>
              <Grid item xs={12} md={4}>
                <SkeletonLine variant="rounded" height={50} width="250px" />
              </Grid>
            </Grid>
          </SkeletonCard>

          {/* Table Card Skeleton */}
          <Card
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              background: isDark
                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.2))"
                : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))",
              backdropFilter: "blur(10px)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
            }}
          >
            <TableContainer
              sx={{ maxHeight: "calc(100vh - 400px)", overflow: "auto" }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "#",
                      "DATE",
                      "SUPPLIER",
                      "ITEMS",
                      "AMOUNT",
                      "FINE",
                      "ACTIONS",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          backgroundColor: isDark
                            ? "rgba(30, 41, 59, 0.95)"
                            : "rgba(248, 250, 252, 0.95)",
                          fontWeight: 700,
                          borderBottom: `2px solid ${
                            isDark
                              ? "rgba(71, 85, 105, 0.3)"
                              : "rgba(226, 232, 240, 0.4)"
                          }`,
                          py: 1.5,
                        }}
                      >
                        <SkeletonLine
                          width={header === "SUPPLIER" ? 80 : 60}
                          height={16}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <SkeletonTableRow key={i} />
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          p: 2,
                        }}
                      >
                        <SkeletonLine
                          variant="rounded"
                          width={200}
                          height={40}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};
// Custom Hooks
const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Safe date formatting utility
const formatDateSafely = (dateStr) => {
  if (!dateStr) return "N/A";

  try {
    let date;

    if (dateStr instanceof Date) {
      date = dateStr;
    } else if (typeof dateStr === "string") {
      const parts = dateStr.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          date = new Date(year, month, day);
        }
      }

      if (!date || isNaN(date.getTime())) {
        date = new Date(dateStr);
      }
    }

    if (date && !isNaN(date.getTime())) {
      return format(date, "dd/MM/yyyy");
    }

    return dateStr;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateStr;
  }
};

// Table Pagination Actions Component
function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <FirstPage />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        size="small"
        sx={{
          color: theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <LastPage />
      </IconButton>
    </Box>
  );
}

// Amount Renderer Component
const AmountCell = ({ value }) => {
  const amount = value || 0;

  return (
    <Box
      sx={{
        textAlign: "right",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          background: "linear-gradient(135deg, #2563EB, #3B82F6)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {new Intl.NumberFormat("en-IN", {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 0,
        }).format(amount)}
      </Typography>
    </Box>
  );
};

// Fine Renderer Component
const FineCell = ({ value, pendingFine }) => {
  const fineValue = value || 0;

  return (
    <Box
      sx={{
        textAlign: "right",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          background: "linear-gradient(135deg, #F59E0B, #D97706)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {fineValue.toFixed(2)}g
      </Typography>
      {pendingFine > 0 && (
        <Typography
          variant="caption"
          sx={{
            color: "#DC2626",
            fontSize: "0.7rem",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: "2px",
            mt: "2px",
          }}
        >
          <ArrowUpwardIcon sx={{ fontSize: "10px" }} />
          {pendingFine.toFixed(2)}g due
        </Typography>
      )}
    </Box>
  );
};

// Items Count Renderer
const ItemsCountCell = ({ count }) => {
  return (
    <Box
      sx={{ textAlign: "center", display: "flex", justifyContent: "center" }}
    >
      <Chip
        label={`${count} item${count !== 1 ? "s" : ""}`}
        size="small"
        variant="filled"
        sx={{
          background:
            "linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(167, 139, 250, 0.15))",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(139, 92, 246, 0.3)",
          color: "#8B5CF6",
          fontWeight: 600,
          borderRadius: "8px",
          minWidth: "70px",
          "&:hover": {
            background:
              "linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(167, 139, 250, 0.25))",
          },
        }}
        icon={<InventoryIcon sx={{ fontSize: "14px", color: "#8B5CF6" }} />}
      />
    </Box>
  );
};

const SupplierTransactionHistory = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const {
    supplierTransactions,
    suppliers,
    loading: contextLoading,
    refresh,
  } = useContext(DataContext);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("date");
  const [order, setOrder] = useState("desc");

  // States
  const isSearching = false;
  const isFiltering = false;
  const [isExporting, setIsExporting] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null,
  });
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Debounced search
  const searchTerm = useDebounce(searchInput, 300);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Handle initial loading - wait for data to load
  useEffect(() => {
    // If context is still loading, keep skeleton visible
    if (contextLoading) {
      setIsInitialLoading(true);
    }
    // If data is loaded and we have transactions or suppliers
    else if (!contextLoading && (supplierTransactions || suppliers)) {
      // Add a small delay for smooth transition
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [contextLoading, supplierTransactions, suppliers]);

  // const handleRefresh = useCallback(async () => {
  //   setIsInitialLoading(true);
  //   await refresh();
  //   // The loading state will be handled by the useEffect above
  // }, [refresh]);

  // Initialize default dates (current financial year)
  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    let startDate, endDate;

    if (currentMonth >= 3) {
      startDate = new Date(currentYear, 3, 1);
      endDate = today;
    } else {
      startDate = new Date(currentYear - 1, 3, 1);
      endDate = today;
    }

    setDates({ startDate, endDate });
  }, []);

  // Safe date parsing
  const parseDateSafely = useCallback((dateStr) => {
    if (!dateStr) return null;

    try {
      if (dateStr instanceof Date) {
        return dateStr;
      }

      if (typeof dateStr === "string") {
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);

          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime())) {
              return date;
            }
          }
        }

        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }

      return null;
    } catch (error) {
      console.error("Error parsing date:", error);
      return null;
    }
  }, []);

  // Parse date for sorting
  const parseDateForSort = (dateStr) => {
    try {
      if (!dateStr) return new Date(0);
      if (dateStr instanceof Date) return dateStr;

      const parts = dateStr.split("/");
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          return new Date(year, month, day);
        }
      }
      return new Date(dateStr);
    } catch {
      return new Date(0);
    }
  };

  // Apply all filters
  const applyFilters = useCallback(() => {
    function filterByDateRange(records, startDate, endDate) {
      if (!records || records.length === 0) return [];
      if (!startDate || !endDate) return records;

      const start = startDate;
      const end = endDate;

      if (!start || !end) {
        return records;
      }

      const endOfDay = new Date(end);
      endOfDay.setHours(23, 59, 59, 999);

      return records.filter((record) => {
        if (!record.date) return false;

        const recordDate = parseDateSafely(record.date);
        if (!recordDate) return false;

        return recordDate >= start && recordDate <= endOfDay;
      });
    }

    if (!supplierTransactions || supplierTransactions.length === 0) {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...supplierTransactions];

    // 1. Filter by supplier
    if (supplierFilter !== "all") {
      filtered = filtered.filter(
        (transaction) => transaction.supplier === supplierFilter
      );
    }

    // 2. Filter by date range
    if (dates.startDate && dates.endDate) {
      filtered = filterByDateRange(filtered, dates.startDate, dates.endDate);
    }

    // 3. Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((transaction) => {
        const supplier = suppliers.find((s) => s.id === transaction.supplier);

        const supplierNameMatch = supplier?.name?.toLowerCase().includes(term);
        const supplierPhoneMatch = supplier?.contact?.includes(term);
        const itemsMatch = transaction.items?.some(
          (item) =>
            item.itemName?.toLowerCase().includes(term) ||
            item.type?.toLowerCase().includes(term)
        );
        const dateMatch = transaction.date?.toLowerCase().includes(term);

        return (
          supplierNameMatch || supplierPhoneMatch || itemsMatch || dateMatch
        );
      });
    }

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "date":
          aValue = parseDateForSort(a.date);
          bValue = parseDateForSort(b.date);
          break;
        case "amount":
          aValue = a.summary?.grandTotal || 0;
          bValue = b.summary?.grandTotal || 0;
          break;
        case "fine":
          aValue = a.summary?.fineBalance || 0;
          bValue = b.summary?.fineBalance || 0;
          break;
        default:
          aValue = parseDateForSort(a.date);
          bValue = parseDateForSort(b.date);
      }

      if (order === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredTransactions(filtered);
    setPage(0);
  }, [
    supplierTransactions,
    supplierFilter,
    dates.startDate,
    dates.endDate,
    searchTerm,
    suppliers,
    parseDateSafely,
    orderBy,
    order,
  ]);

  useEffect(() => {
    applyFilters();
  }, [
    supplierTransactions,
    supplierFilter,
    dates.startDate,
    dates.endDate,
    searchTerm,
    applyFilters,
  ]);

  // Handle Sort
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Show Alert
  const showAlert = useCallback((message, type) => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 3000);
  }, []);

  // Handle Export PDF
  const handleExportPDF = useCallback(async () => {
    if (filteredTransactions.length === 0) {
      showAlert("No transactions to export", "warning");
      return;
    }

    setIsExporting(true);

    try {
      showAlert("Generating PDF...", "info");

      const pdf = new jsPDF("p", "mm", "a4");

      if (filteredTransactions.length === 0) {
        showAlert("No transactions found for export", "warning");
        return;
      }

      for (let i = 0; i < filteredTransactions.length; i++) {
        if (i > 0) {
          pdf.addPage();
        }

        const transaction = filteredTransactions[i];
        const supplier =
          suppliers.find((s) => s.id === transaction.supplier) || {};
        const billHtml = generateSupplierBillHTML(transaction, supplier);

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = billHtml;
        tempDiv.style.width = "210mm";
        tempDiv.style.padding = "0";
        tempDiv.style.margin = "0";
        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
          scale: 2,
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/jpeg", 0.9);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = imgWidth / imgHeight;

        const margin = 5;
        const maxWidth = pageWidth - 2 * margin;
        const maxHeight = pageHeight - 2 * margin;

        let finalWidth, finalHeight;

        if (imgWidth > imgHeight) {
          finalWidth = maxWidth;
          finalHeight = maxWidth / ratio;
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = maxHeight * ratio;
          }
        } else {
          finalHeight = maxHeight;
          finalWidth = maxHeight * ratio;
          if (finalWidth > maxWidth) {
            finalWidth = maxWidth;
            finalHeight = maxWidth / ratio;
          }
        }

        const x = (pageWidth - finalWidth) / 2;
        const y = (pageHeight - finalHeight) / 2;

        pdf.addImage(imgData, "JPEG", x, y, finalWidth, finalHeight);
        document.body.removeChild(tempDiv);
      }

      const fileName = `Supplier_Transaction_History_${format(
        dates.startDate,
        "dd-MM-yyyy"
      )}_to_${format(dates.endDate, "dd-MM-yyyy")}.pdf`;
      pdf.save(fileName);

      showAlert("PDF exported successfully", "success");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showAlert("Failed to generate PDF", "error");
    } finally {
      setIsExporting(false);
    }
  }, [dates, suppliers, showAlert, filteredTransactions]);

  // Handle Export Excel
  const handleExportExcel = useCallback(() => {
    if (filteredTransactions.length === 0) {
      showAlert("No transactions to export", "warning");
      return;
    }

    try {
      const excelData = filteredTransactions.map((transaction, index) => {
        const supplier = suppliers.find((s) => s.id === transaction.supplier);
        return {
          "Sr. No.": index + 1,
          Date: formatDateSafely(transaction.date),
          "Supplier Name": supplier?.name || "N/A",
          "Items Count": transaction.items?.length || 0,
          Subtotal: transaction.summary?.subTotal || 0,
          CGST: transaction.summary?.cgst || 0,
          SGST: transaction.summary?.sgst || 0,
          "Grand Total": transaction.summary?.grandTotal || 0,
          Paid: transaction.summary?.paymentDone || 0,
          "Pending Amount": transaction.summary?.pendingAmount || 0,
          "Fine Balance": transaction.summary?.fineBalance || 0,
          "Fine Payment": transaction.summary?.finePayment || 0,
          "Pending Fine": transaction.summary?.pendingFine || 0,
          Status:
            (transaction.summary?.pendingAmount || 0) === 0
              ? "Paid"
              : "Pending",
          GST: transaction.gstApplicable ? "Yes" : "No",
        };
      });

      const totals = {
        "Sr. No.": "TOTAL",
        Date: "",
        "Supplier Name": "",
        "Items Count": excelData.reduce(
          (sum, row) => sum + row["Items Count"],
          0
        ),
        Subtotal: excelData.reduce((sum, row) => sum + row["Subtotal"], 0),
        CGST: excelData.reduce((sum, row) => sum + row["CGST"], 0),
        SGST: excelData.reduce((sum, row) => sum + row["SGST"], 0),
        "Grand Total": excelData.reduce(
          (sum, row) => sum + row["Grand Total"],
          0
        ),
        Paid: excelData.reduce((sum, row) => sum + row["Paid"], 0),
        "Pending Amount": excelData.reduce(
          (sum, row) => sum + row["Pending Amount"],
          0
        ),
        "Fine Balance": excelData.reduce(
          (sum, row) => sum + row["Fine Balance"],
          0
        ),
        "Fine Payment": excelData.reduce(
          (sum, row) => sum + row["Fine Payment"],
          0
        ),
        "Pending Fine": excelData.reduce(
          (sum, row) => sum + row["Pending Fine"],
          0
        ),
        Status: "",
        GST: "",
      };

      excelData.push({});
      excelData.push(totals);

      const headers = Object.keys(excelData[0]);
      const dataWithHeaders = [
        headers,
        ...excelData.map((row) => headers.map((key) => row[key] || "")),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);

      const colWidths = [
        { wch: 8 },
        { wch: 12 },
        { wch: 25 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 10 },
        { wch: 8 },
      ];
      worksheet["!cols"] = colWidths;

      const headerRange = XLSX.utils.decode_range(worksheet["!ref"]);
      headerRange.s.r = 0;
      headerRange.e.r = 0;

      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "F0F0F0" } },
          alignment: { horizontal: "center" },
        };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

      const fileName = `Supplier_Transaction_History_${format(
        dates.startDate,
        "dd-MM-yyyy"
      )}_to_${format(dates.endDate, "dd-MM-yyyy")}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      showAlert("Excel file exported successfully", "success");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      showAlert("Failed to export Excel file", "error");
    }
  }, [suppliers, dates, showAlert, filteredTransactions]);

  // Handle Clear Filters
  const handleClearFilters = useCallback(() => {
    setSupplierFilter("all");
    setSearchInput("");
    showAlert("Filters cleared", "info");
  }, [showAlert]);

  // Helper function to get supplier name
  const getSupplierName = useCallback(
    (supplierId) => {
      const supplier = suppliers.find((s) => s.id === supplierId);
      return supplier?.name || "N/A";
    },
    [suppliers]
  );

  // Get supplier object
  const getSupplier = (supplierId) => {
    return suppliers.find((s) => s.id === supplierId);
  };

  // Loading states
  const isLoading = contextLoading || isInitialLoading;
  const isProcessing = isSearching || isFiltering || isExporting;

  // Get current page data
  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Add this state with other states

  return (
    <>
      {isLoading ? (
        <SupplierTransactionHistorySkeleton />
      ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ minHeight: "100vh", py: 4 }}>
            {/* Backdrop for loading states */}
            <Backdrop
              open={isExporting}
              sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                color: "#fff",
                backdropFilter: "blur(10px)",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress color="inherit" size={50} />
                <Typography sx={{ mt: 2, fontWeight: 600 }}>
                  Generating PDF...
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                  This may take a moment
                </Typography>
              </Box>
            </Backdrop>

            <Container maxWidth="xl">
              {/* Alert */}
              {alertMessage && (
                <Fade in={!!alertMessage}>
                  <Alert
                    severity={alertType}
                    sx={{
                      mb: 3,
                      borderRadius: "12px",
                      backdropFilter: "blur(10px)",
                      border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                      backgroundColor: isDark
                        ? "rgba(30, 41, 59, 0.7)"
                        : "rgba(255, 255, 255, 0.8)",
                    }}
                    onClose={() => setAlertMessage("")}
                  >
                    {alertMessage}
                  </Alert>
                </Fade>
              )}

              {/* Header */}
              <Box
                sx={{
                  mb: 4,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", md: "center" },
                  gap: 3,
                }}
              >
                <Box>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      mb: 0.5,
                      background: isDark
                        ? "linear-gradient(135deg, #F1F5F9, #E2E8F0)"
                        : "linear-gradient(135deg, #1E293B, #374151)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Supplier Transaction History
                  </Typography>
                  <Typography
                    sx={{
                      color: isDark ? "#A0A0A0" : "#6B7280",
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <HistoryIcon fontSize="small" />
                    View and manage supplier transactions and payments
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: { xs: "center", md: "flex-end" },
                  }}
                >
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={refresh}
                    variant="outlined"
                    disabled={isFiltering}
                    sx={{
                      borderColor:
                        theme.palette.mode === "dark" ? "#60A5FA" : "#2563EB",
                      color: theme.palette.mode === "dark" ? "#60A5FA" : "#fff",
                      borderRadius: "10px",
                      backdropFilter: "blur(10px)",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(37, 99, 235, 0.8)",
                      borderWidth: "1.5px",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    variant="outlined"
                    disabled={supplierFilter === "all" && !searchInput}
                    sx={{
                      borderColor:
                        theme.palette.mode === "dark" ? "#94A3B8" : "#6B7280",
                      color: theme.palette.mode === "dark" ? "#E2E8F0" : "#fff",
                      borderRadius: "10px",
                      backdropFilter: "blur(10px)",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(107, 114, 128, 0.8)",
                      borderWidth: "1.5px",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                  >
                    Clear Filters
                  </Button>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleExportExcel}
                    variant="outlined"
                    disabled={filteredTransactions.length === 0}
                    sx={{
                      borderColor:
                        theme.palette.mode === "dark" ? "#34D399" : "#10B981",
                      color: theme.palette.mode === "dark" ? "#34D399" : "#fff",
                      borderRadius: "10px",
                      backdropFilter: "blur(10px)",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(30, 41, 59, 0.8)"
                          : "rgba(16, 185, 129, 0.8)",
                      borderWidth: "1.5px",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                  >
                    Export Excel
                  </Button>
                  <Button
                    startIcon={<PdfIcon />}
                    onClick={handleExportPDF}
                    variant="contained"
                    disabled={isExporting || filteredTransactions.length === 0}
                    sx={{
                      background: "linear-gradient(135deg, #DC2626, #B91C1C)",
                      borderRadius: "10px",
                      boxShadow: "0 4px 12px rgba(220, 38, 38, 0.4)",
                      fontWeight: 600,
                      transition: "all 0.2s ease",
                    }}
                  >
                    Export PDF
                  </Button>
                </Box>
              </Box>

              {/* Stats Summary - Keep exactly as original */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                      backdropFilter: "blur(10px)",
                      border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, rgba(37, 99, 235, 0.15), rgba(59, 130, 246, 0.15))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <ReceiptIcon sx={{ color: "#2563EB" }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Transactions
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#2563EB", mb: 1 }}
                      >
                        {filteredTransactions.length}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {filteredTransactions.length > 0
                          ? `${(
                              (filteredTransactions.length /
                                supplierTransactions.length) *
                              100
                            ).toFixed(1)}% of total`
                          : "No transactions"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                      backdropFilter: "blur(10px)",
                      border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(34, 197, 94, 0.15))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <TrendingUpIcon sx={{ color: "#10B981" }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Amount
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#10B981", mb: 1 }}
                      >
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          minimumFractionDigits: 0,
                        }).format(
                          filteredTransactions.reduce(
                            (sum, t) => sum + (t.summary?.grandTotal || 0),
                            0
                          )
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avg:{" "}
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          minimumFractionDigits: 0,
                        }).format(
                          filteredTransactions.length > 0
                            ? filteredTransactions.reduce(
                                (sum, t) => sum + (t.summary?.grandTotal || 0),
                                0
                              ) / filteredTransactions.length
                            : 0
                        )}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                      backdropFilter: "blur(10px)",
                      border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <PaymentIcon sx={{ color: "#EF4444" }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Pending
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#EF4444", mb: 1 }}
                      >
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          minimumFractionDigits: 0,
                        }).format(
                          filteredTransactions.reduce(
                            (sum, t) => sum + (t.summary?.pendingAmount || 0),
                            0
                          )
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {
                          filteredTransactions.filter(
                            (t) => t.summary?.pendingAmount > 0
                          ).length
                        }{" "}
                        pending
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: "16px",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                      backdropFilter: "blur(10px)",
                      border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            background:
                              "linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <ScaleIcon sx={{ color: "#F59E0B" }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Total Fine Balance
                        </Typography>
                      </Box>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 800, color: "#F59E0B", mb: 1 }}
                      >
                        {filteredTransactions
                          .reduce(
                            (sum, t) => sum + (t.summary?.fineBalance || 0),
                            0
                          )
                          .toFixed(2)}
                        g
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {
                          filteredTransactions.filter(
                            (t) => t.summary?.pendingFine > 0
                          ).length
                        }{" "}
                        with pending fine
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Active Filters Summary */}
              {(supplierFilter !== "all" || searchInput) && (
                <Fade in>
                  <Box
                    sx={{
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      padding: 2,
                      borderRadius: "12px",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                      backdropFilter: "blur(10px)",
                      border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <FilterIcon sx={{ color: "#6B7280", fontSize: "1rem" }} />
                    <Typography variant="body2" color="text.secondary">
                      Active filters:
                    </Typography>
                    {supplierFilter !== "all" && (
                      <Chip
                        label={`Supplier: ${
                          suppliers.find((s) => s.id === supplierFilter)
                            ?.name || "Selected Supplier"
                        }`}
                        size="small"
                        onDelete={() => setSupplierFilter("all")}
                        color="primary"
                        variant="filled"
                        sx={{
                          backdropFilter: "blur(10px)",
                          backgroundColor: "rgba(37, 99, 235, 0.1)",
                          border: "1px solid rgba(37, 99, 235, 0.3)",
                          color: "#2563EB",
                          fontWeight: 500,
                        }}
                      />
                    )}
                    {searchInput && (
                      <Chip
                        label={`Search: "${searchInput}"`}
                        size="small"
                        onDelete={() => setSearchInput("")}
                        color="secondary"
                        variant="filled"
                        sx={{
                          backdropFilter: "blur(10px)",
                          backgroundColor: "rgba(139, 92, 246, 0.1)",
                          border: "1px solid rgba(139, 92, 246, 0.3)",
                          color: "#8B5CF6",
                          fontWeight: 500,
                        }}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: "auto" }}
                    >
                      Showing {filteredTransactions.length} of{" "}
                      {supplierTransactions.length} transactions
                    </Typography>
                  </Box>
                </Fade>
              )}

              {/* Filters Section */}
              <Card
                sx={{
                  mb: 4,
                  borderRadius: "16px",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.6))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.6))",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Start Date"
                        value={dates.startDate}
                        onChange={(newValue) =>
                          setDates((prev) => ({ ...prev, startDate: newValue }))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarIcon />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                backdropFilter: "blur(10px)",
                                backgroundColor: isDark
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.02)",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="End Date"
                        value={dates.endDate}
                        onChange={(newValue) =>
                          setDates((prev) => ({ ...prev, endDate: newValue }))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <CalendarIcon />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                backdropFilter: "blur(10px)",
                                backgroundColor: isDark
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.02)",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        size="small"
                        options={[
                          { id: "all", name: "All Suppliers" },
                          ...suppliers,
                        ]}
                        value={
                          supplierFilter === "all"
                            ? { id: "all", name: "All Suppliers" }
                            : suppliers.find((s) => s.id === supplierFilter) ||
                              null
                        }
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        onChange={(event, newValue) =>
                          setSupplierFilter(newValue ? newValue.id : "all")
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Supplier"
                            placeholder="Search by name, phone..."
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                backdropFilter: "blur(10px)",
                                backgroundColor: isDark
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.02)",
                                width: "300px",
                              },
                            }}
                          />
                        )}
                        renderOption={(props, option) => {
                          if (option.id === "all") {
                            return (
                              <Box component="li" {...props} key="all">
                                <Typography
                                  variant="body2"
                                  sx={{ fontStyle: "italic" }}
                                >
                                  {option.name}
                                </Typography>
                              </Box>
                            );
                          }
                          return (
                            <Box component="li" {...props} key={option.id}>
                              <Box sx={{ width: "100%" }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {option.name}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                                  {option.contact && (
                                    <Chip
                                      size="small"
                                      label={option.contact}
                                      variant="outlined"
                                      sx={{
                                        height: 20,
                                        fontSize: "0.7rem",
                                        backdropFilter: "blur(10px)",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        }}
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options.slice(0, 50);
                          const searchTerm = inputValue.toLowerCase();
                          return options.filter((option) => {
                            if (option.id === "all") return true;
                            return (
                              option.name.toLowerCase().includes(searchTerm) ||
                              option.contact?.toString()?.includes(searchTerm)
                            );
                          });
                        }}
                        noOptionsText={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 2, textAlign: "center" }}
                          >
                            No matching suppliers found
                          </Typography>
                        }
                        loading={suppliers.length === 0}
                        loadingText="Loading suppliers..."
                        blurOnSelect
                        clearOnBlur={false}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Custom Table */}
              <Card
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  position: "relative",
                  background: isDark
                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(30, 41, 59, 0.2))"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.2))",
                  backdropFilter: "blur(10px)",
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.08)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                }}
              >
                {isProcessing && (
                  <LinearProgress
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: 2,
                      height: 3,
                    }}
                  />
                )}

                <TableContainer
                  sx={{ maxHeight: "calc(100vh - 400px)", overflow: "auto" }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.95)"
                              : "rgba(248, 250, 252, 0.95)",
                            fontWeight: 700,
                            color: isDark ? "#94A3B8" : "#64748B",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: `2px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            py: 1.5,
                            width: 50,
                          }}
                        >
                          #
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.95)"
                              : "rgba(248, 250, 252, 0.95)",
                            fontWeight: 700,
                            color: isDark ? "#94A3B8" : "#64748B",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: `2px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            py: 1.5,
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === "date"}
                            direction={orderBy === "date" ? order : "asc"}
                            onClick={() => handleSort("date")}
                          >
                            DATE
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.95)"
                              : "rgba(248, 250, 252, 0.95)",
                            fontWeight: 700,
                            color: isDark ? "#94A3B8" : "#64748B",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: `2px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            py: 1.5,
                            minWidth: 250,
                          }}
                        >
                          SUPPLIER
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.95)"
                              : "rgba(248, 250, 252, 0.95)",
                            fontWeight: 700,
                            color: isDark ? "#94A3B8" : "#64748B",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: `2px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            py: 1.5,
                            align: "center",
                          }}
                        >
                          ITEMS
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.95)"
                              : "rgba(248, 250, 252, 0.95)",
                            fontWeight: 700,
                            color: isDark ? "#94A3B8" : "#64748B",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: `2px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            py: 1.5,
                            align: "right",
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === "amount"}
                            direction={orderBy === "amount" ? order : "asc"}
                            onClick={() => handleSort("amount")}
                          >
                            AMOUNT
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.95)"
                              : "rgba(248, 250, 252, 0.95)",
                            fontWeight: 700,
                            color: isDark ? "#94A3B8" : "#64748B",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: `2px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            py: 1.5,
                            align: "right",
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === "fine"}
                            direction={orderBy === "fine" ? order : "asc"}
                            onClick={() => handleSort("fine")}
                          >
                            FINE
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.95)"
                              : "rgba(248, 250, 252, 0.95)",
                            fontWeight: 700,
                            color: isDark ? "#94A3B8" : "#64748B",
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            borderBottom: `2px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            py: 1.5,
                            align: "center",
                          }}
                        >
                          ACTIONS
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedTransactions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: isDark
                                    ? "linear-gradient(135deg, rgba(30, 41, 59, 0.4), rgba(30, 41, 59, 0.6))"
                                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.9))",
                                  backdropFilter: "blur(10px)",
                                }}
                              >
                                <ReceiptIcon
                                  sx={{
                                    fontSize: 40,
                                    color: isDark ? "#94A3B8" : "#9CA3AF",
                                    opacity: 0.6,
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: isDark ? "#A0A0A0" : "#666",
                                  fontWeight: 500,
                                }}
                              >
                                {searchInput
                                  ? "No transactions found"
                                  : "No transactions available"}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isDark ? "#71717A" : "#9CA3AF",
                                  textAlign: "center",
                                  maxWidth: 300,
                                }}
                              >
                                {searchInput
                                  ? "Try adjusting your search or filters to find what you're looking for."
                                  : "Supplier transactions will appear here once you create them."}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedTransactions.map((transaction, idx) => {
                          const supplier = getSupplier(transaction.supplier);
                          const itemsCount = transaction.items?.length || 0;
                          const pendingAmount =
                            transaction.summary?.pendingAmount || 0;
                          const pendingFine =
                            transaction.summary?.pendingFine || 0;
                          const isPaid =
                            pendingAmount === 0 && pendingFine === 0;

                          return (
                            <TableRow
                              key={transaction.id}
                              hover
                              sx={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: isDark
                                    ? "rgba(96, 165, 250, 0.08)"
                                    : "rgba(37, 99, 235, 0.04)",
                                  transform: "translateY(-1px)",
                                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                                  position: "relative",
                                  zIndex: 1,
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  fontWeight: 600,
                                  color: "#6B7280",
                                }}
                              >
                                {page * rowsPerPage + idx + 1}
                              </TableCell>
                              <TableCell
                                sx={{
                                  py: 1.5,
                                  fontWeight: 600,
                                  color: isDark ? "#E2E8F0" : "#1F2937",
                                  fontFamily: "'SF Mono', monospace",
                                }}
                              >
                                {formatDateSafely(transaction.date)}
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: "12px",
                                      background:
                                        "linear-gradient(135deg, #8B5CF6, #A78BFA)",
                                      color: "white",
                                      fontWeight: 600,
                                      fontSize: "1rem",
                                      boxShadow:
                                        "0 2px 8px rgba(139, 92, 246, 0.3)",
                                    }}
                                  >
                                    {supplier?.name?.charAt(0) || "S"}
                                  </Avatar>
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, lineHeight: 1.3 }}
                                    >
                                      {supplier?.name || "N/A"}
                                    </Typography>
                                    {supplier?.contact && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                          fontSize: "0.7rem",
                                        }}
                                      >
                                        {supplier.contact}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ py: 1.5 }}>
                                <ItemsCountCell count={itemsCount} />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1.5 }}>
                                <AmountCell
                                  value={transaction.summary?.grandTotal}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ py: 1.5 }}>
                                <FineCell
                                  value={transaction.summary?.fineBalance}
                                  pendingFine={transaction.summary?.pendingFine}
                                />
                              </TableCell>
                              <TableCell align="center" sx={{ py: 1.5 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    justifyContent: "center",
                                  }}
                                >
                                  <Tooltip title="View Details" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setSelectedTransaction(transaction);
                                        setOpenModal(true);
                                      }}
                                      sx={{
                                        color: "#2563EB",
                                        backgroundColor:
                                          "rgba(37, 99, 235, 0.1)",
                                        borderRadius: "8px",
                                        padding: "6px",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(37, 99, 235, 0.2)",
                                          transform: "translateY(-2px)",
                                        },
                                      }}
                                    >
                                      <ViewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Chip
                                    label={isPaid ? "Paid" : "Pending"}
                                    size="small"
                                    sx={{
                                      fontWeight: 600,
                                      borderRadius: "8px",
                                      minWidth: "80px",
                                      backgroundColor: isPaid
                                        ? "rgba(16, 185, 129, 0.15)"
                                        : "rgba(245, 158, 11, 0.15)",
                                      border: isPaid
                                        ? "1px solid rgba(16, 185, 129, 0.3)"
                                        : "1px solid rgba(245, 158, 11, 0.3)",
                                      color: isPaid ? "#10B981" : "#F59E0B",
                                    }}
                                    icon={
                                      isPaid ? (
                                        <CheckCircleOutlineIcon
                                          sx={{ fontSize: "14px" }}
                                        />
                                      ) : (
                                        <ErrorOutlineIcon
                                          sx={{ fontSize: "14px" }}
                                        />
                                      )
                                    }
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          colSpan={7}
                          count={filteredTransactions.length}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={(event, newPage) => setPage(newPage)}
                          onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                          }}
                          ActionsComponent={TablePaginationActions}
                          sx={{
                            borderTop: `1px solid ${
                              isDark
                                ? "rgba(71, 85, 105, 0.3)"
                                : "rgba(226, 232, 240, 0.4)"
                            }`,
                            backgroundColor: isDark
                              ? "rgba(30, 41, 59, 0.8)"
                              : "rgba(248, 250, 252, 0.8)",
                          }}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Card>

              {/* Transaction Details Modal */}
              <Dialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: "24px",
                    background: isDark
                      ? "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)"
                      : "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)",
                    backdropFilter: "blur(30px)",
                    border: `1px solid ${alpha(
                      isDark ? "#475569" : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: "0 25px 80px rgba(0, 0, 0, 0.25)",
                    maxHeight: "80vh",
                    overflow: "hidden",
                  },
                }}
              >
                {selectedTransaction && (
                  <>
                    {/* Header Section */}
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: `1px solid ${alpha(
                          isDark ? "#475569" : "#e2e8f0",
                          isDark ? 0.2 : 0.3
                        )}`,
                        background: isDark
                          ? "linear-gradient(90deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)"
                          : "linear-gradient(90deg, rgba(37, 99, 235, 0.08) 0%, rgba(29, 78, 216, 0.04) 100%)",
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "4px",
                          background:
                            "linear-gradient(90deg, #60a5fa, #3b82f6, #60a5fa)",
                          animation: "shine 3s infinite linear",
                        },
                        "@keyframes shine": {
                          "0%": { backgroundPosition: "-200px 0" },
                          "100%": {
                            backgroundPosition: "calc(200px + 100%) 0",
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 45,
                                height: 45,
                                borderRadius: "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                  "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)",
                                boxShadow: "0 8px 32px rgba(96, 165, 250, 0.3)",
                              }}
                            >
                              <ReceiptIcon
                                sx={{ fontSize: 25, color: "white" }}
                              />
                            </Box>
                            <Box>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 800,
                                  color: isDark ? "#f1f5f9" : "#1e293b",
                                  mb: 0.5,
                                }}
                              >
                                Supplier Transaction Details
                              </Typography>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  fontWeight: 500,
                                }}
                              >
                                <CalendarTodayIcon sx={{ fontSize: 18 }} />
                                Date:{" "}
                                {selectedTransaction
                                  ? formatDateSafely(selectedTransaction.date)
                                  : "N/A"}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Status & Info Chips */}
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              flexWrap: "wrap",
                              mt: 2,
                            }}
                          >
                            <Chip
                              icon={<PersonIcon />}
                              label={`Supplier: ${getSupplierName(
                                selectedTransaction.supplier
                              )}`}
                              sx={{
                                backgroundColor: alpha("#10b981", 0.15),
                                color: "#10b981",
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                border: `1px solid ${alpha("#10b981", 0.3)}`,
                                backdropFilter: "blur(10px)",
                              }}
                            />
                            <Chip
                              icon={
                                selectedTransaction.gstApplicable ? "📋" : "📄"
                              }
                              label={`GST: ${
                                selectedTransaction.gstApplicable
                                  ? "Applicable"
                                  : "Not Applicable"
                              }`}
                              sx={{
                                backgroundColor:
                                  selectedTransaction.gstApplicable
                                    ? alpha("#059669", 0.15)
                                    : alpha("#6b7280", 0.15),
                                color: selectedTransaction.gstApplicable
                                  ? "#059669"
                                  : "#6b7280",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                border: `1px solid ${alpha(
                                  selectedTransaction.gstApplicable
                                    ? "#059669"
                                    : "#6b7280",
                                  0.3
                                )}`,
                                backdropFilter: "blur(10px)",
                              }}
                            />
                            <Chip
                              icon={
                                selectedTransaction.items?.[0]?.type
                                  ? "📦"
                                  : "📄"
                              }
                              label={`Type: ${
                                selectedTransaction.items?.[0]?.type || "Mixed"
                              }`}
                              sx={{
                                backgroundColor: alpha("#8b5cf6", 0.15),
                                color: "#8b5cf6",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                border: `1px solid ${alpha("#8b5cf6", 0.3)}`,
                                backdropFilter: "blur(10px)",
                              }}
                            />
                            <Chip
                              icon={
                                selectedTransaction.summary?.pendingAmount > 0
                                  ? "⏳"
                                  : "✅"
                              }
                              label={
                                selectedTransaction.summary?.pendingAmount > 0
                                  ? `Pending: ₹${selectedTransaction.summary.pendingAmount.toLocaleString(
                                      "en-IN"
                                    )}`
                                  : "Fully Paid"
                              }
                              sx={{
                                backgroundColor:
                                  selectedTransaction.summary?.pendingAmount > 0
                                    ? alpha("#f59e0b", 0.15)
                                    : alpha("#10b981", 0.15),
                                color:
                                  selectedTransaction.summary?.pendingAmount > 0
                                    ? "#f59e0b"
                                    : "#10b981",
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                border: `1px solid ${alpha(
                                  selectedTransaction.summary?.pendingAmount > 0
                                    ? "#f59e0b"
                                    : "#10b981",
                                  0.3
                                )}`,
                                backdropFilter: "blur(10px)",
                              }}
                            />
                          </Box>
                        </Box>

                        <IconButton
                          onClick={() => setOpenModal(false)}
                          sx={{
                            backgroundColor: isDark
                              ? "rgba(255, 255, 255, 0.1)"
                              : "rgba(0, 0, 0, 0.05)",
                            color: isDark ? "#94a3b8" : "#64748b",
                            width: 48,
                            height: 48,
                            borderRadius: "12px",
                            "&:hover": {
                              backgroundColor: isDark
                                ? "rgba(255, 255, 255, 0.2)"
                                : "rgba(0, 0, 0, 0.1)",
                              transform: "rotate(90deg)",
                              transition: "transform 0.3s ease",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ p: 2, overflow: "auto", maxHeight: "60vh" }}>
                      <Grid
                        container
                        spacing={3}
                        sx={{ display: "flex", flexDirection: "column" }}
                      >
                        {/* Left Column - Items Table */}
                        <Grid item xs={12} lg={7}>
                          <Paper
                            sx={{
                              p: 3,
                              borderRadius: "20px",
                              background: isDark
                                ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)"
                                : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)",
                              backdropFilter: "blur(20px)",
                              border: `1px solid ${alpha(
                                isDark ? "#475569" : "#e2e8f0",
                                isDark ? 0.2 : 0.3
                              )}`,
                              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                              height: "100%",
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 3,
                              }}
                            >
                              <Typography
                                variant="h5"
                                sx={{
                                  fontWeight: 800,
                                  color: isDark ? "#f1f5f9" : "#1e293b",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background:
                                      "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                                    boxShadow:
                                      "0 8px 25px rgba(16, 185, 129, 0.3)",
                                  }}
                                >
                                  <InventoryIcon
                                    sx={{ fontSize: 24, color: "white" }}
                                  />
                                </Box>
                                Items Purchased (
                                {selectedTransaction.items?.length || 0})
                              </Typography>
                            </Box>

                            {/* Group items by type */}
                            {(() => {
                              const groupedItems = {
                                piece:
                                  selectedTransaction.items?.filter(
                                    (item) => item.type === "piece"
                                  ) || [],
                                melting:
                                  selectedTransaction.items?.filter(
                                    (item) => item.type === "melting"
                                  ) || [],
                                pure:
                                  selectedTransaction.items?.filter(
                                    (item) => item.type === "pure"
                                  ) || [],
                                fine:
                                  selectedTransaction.items?.filter(
                                    (item) => item.type === "fine"
                                  ) || [],
                              };

                              const nonEmptyGroups = Object.entries(
                                groupedItems
                              ).filter(([_, items]) => items.length > 0);

                              if (nonEmptyGroups.length === 0) {
                                return (
                                  <Box
                                    sx={{
                                      textAlign: "center",
                                      py: 6,
                                      borderRadius: "12px",
                                      background: isDark
                                        ? "rgba(255, 255, 255, 0.02)"
                                        : "rgba(0, 0, 0, 0.02)",
                                      border: `1px dashed ${alpha(
                                        isDark ? "#475569" : "#e2e8f0",
                                        isDark ? 0.2 : 0.3
                                      )}`,
                                    }}
                                  >
                                    <InventoryIcon
                                      sx={{
                                        fontSize: 40,
                                        color: "text.secondary",
                                        mb: 2,
                                        opacity: 0.5,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No items found in this transaction
                                    </Typography>
                                  </Box>
                                );
                              }

                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                  }}
                                >
                                  {nonEmptyGroups.map(([type, typeItems]) => {
                                    const getTypeDisplay = (type) => {
                                      switch (type) {
                                        case "piece":
                                          return "On Piece Items";
                                        case "melting":
                                          return "On Melting Items";
                                        case "pure":
                                          return "On Pure Items";
                                        case "fine":
                                          return "By Fine Items";
                                        default:
                                          return type;
                                      }
                                    };

                                    const calculateItemTotal = (item) => {
                                      if (item.type === "piece") {
                                        return (
                                          (parseFloat(item.count) || 0) *
                                          (parseFloat(item.price) || 0)
                                        );
                                      } else if (item.type === "melting") {
                                        const rate = parseFloat(item.rate) || 0;
                                        const weight =
                                          parseFloat(item.weight) || 0;
                                        const wastage =
                                          parseFloat(item.wastage) || 0;
                                        const percent =
                                          parseFloat(item.percent) || 0;
                                        return (
                                          ((wastage + percent) / 100) *
                                          weight *
                                          rate
                                        );
                                      } else if (item.type === "pure") {
                                        const rate = parseFloat(item.rate) || 0;
                                        const weight =
                                          parseFloat(item.weight) || 0;
                                        const labour =
                                          parseFloat(item.labour) || 0;
                                        return weight * (rate + labour);
                                      } else if (item.type === "fine") {
                                        const melting =
                                          parseFloat(item.melting) || 0;
                                        const wastage =
                                          parseFloat(item.wastage) || 0;
                                        const weight =
                                          parseFloat(item.weight) || 0;
                                        return (
                                          ((melting + wastage) / 100) * weight
                                        );
                                      }
                                      return 0;
                                    };

                                    const typeTotal = typeItems.reduce(
                                      (sum, item) =>
                                        sum + calculateItemTotal(item),
                                      0
                                    );

                                    return (
                                      <Box key={type} sx={{ mb: 2 }}>
                                        {/* Type Header */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: 2,
                                            p: 2,
                                            borderRadius: "12px",
                                            background: isDark
                                              ? `rgba(${
                                                  type === "piece"
                                                    ? "96, 165, 250"
                                                    : type === "melting"
                                                    ? "251, 191, 36"
                                                    : type === "pure"
                                                    ? "52, 211, 153"
                                                    : "167, 139, 250"
                                                }, 0.1)`
                                              : `rgba(${
                                                  type === "piece"
                                                    ? "37, 99, 235"
                                                    : type === "melting"
                                                    ? "245, 158, 11"
                                                    : type === "pure"
                                                    ? "16, 185, 129"
                                                    : "139, 92, 246"
                                                }, 0.1)`,
                                            border: `1px solid ${alpha(
                                              type === "piece"
                                                ? "#60a5fa"
                                                : type === "melting"
                                                ? "#f59e0b"
                                                : type === "pure"
                                                ? "#10b981"
                                                : "#8b5cf6",
                                              0.3
                                            )}`,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1.5,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: "8px",
                                                background: isDark
                                                  ? `rgba(${
                                                      type === "piece"
                                                        ? "96, 165, 250"
                                                        : type === "melting"
                                                        ? "251, 191, 36"
                                                        : type === "pure"
                                                        ? "52, 211, 153"
                                                        : "167, 139, 250"
                                                    }, 0.15)`
                                                  : `rgba(${
                                                      type === "piece"
                                                        ? "37, 99, 235"
                                                        : type === "melting"
                                                        ? "245, 158, 11"
                                                        : type === "pure"
                                                        ? "16, 185, 129"
                                                        : "139, 92, 246"
                                                    }, 0.15)`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color:
                                                  type === "piece"
                                                    ? "#3b82f6"
                                                    : type === "melting"
                                                    ? "#f59e0b"
                                                    : type === "pure"
                                                    ? "#10b981"
                                                    : "#8b5cf6",
                                              }}
                                            >
                                              {type === "piece" ? (
                                                <LocalAtmIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : type === "melting" ? (
                                                <WhatshotIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : type === "pure" ? (
                                                <DiamondIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              ) : (
                                                <ScaleIcon
                                                  sx={{ fontSize: 16 }}
                                                />
                                              )}
                                            </Box>
                                            <Box>
                                              <Typography
                                                variant="subtitle1"
                                                sx={{
                                                  fontWeight: 700,
                                                  color:
                                                    type === "piece"
                                                      ? "#3b82f6"
                                                      : type === "melting"
                                                      ? "#f59e0b"
                                                      : type === "pure"
                                                      ? "#10b981"
                                                      : "#8b5cf6",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                {getTypeDisplay(type)}
                                                <Chip
                                                  label={`${
                                                    typeItems.length
                                                  } item${
                                                    typeItems.length !== 1
                                                      ? "s"
                                                      : ""
                                                  }`}
                                                  size="small"
                                                  sx={{
                                                    height: 20,
                                                    fontSize: "0.7rem",
                                                    fontWeight: 600,
                                                    backgroundColor: isDark
                                                      ? "rgba(255, 255, 255, 0.1)"
                                                      : "rgba(0, 0, 0, 0.05)",
                                                    color:
                                                      type === "piece"
                                                        ? "#3b82f6"
                                                        : type === "melting"
                                                        ? "#f59e0b"
                                                        : type === "pure"
                                                        ? "#10b981"
                                                        : "#8b5cf6",
                                                    border: `1px solid ${alpha(
                                                      type === "piece"
                                                        ? "#3b82f6"
                                                        : type === "melting"
                                                        ? "#f59e0b"
                                                        : type === "pure"
                                                        ? "#10b981"
                                                        : "#8b5cf6",
                                                      0.3
                                                    )}`,
                                                  }}
                                                />
                                              </Typography>
                                            </Box>
                                          </Box>
                                          <Box sx={{ textAlign: "right" }}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "text.secondary",
                                                fontWeight: 600,
                                                display: "block",
                                                mb: 0.5,
                                              }}
                                            >
                                              Type Total
                                            </Typography>
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                fontWeight: 800,
                                                color:
                                                  type === "piece"
                                                    ? "#3b82f6"
                                                    : type === "melting"
                                                    ? "#f59e0b"
                                                    : type === "pure"
                                                    ? "#10b981"
                                                    : "#8b5cf6",
                                              }}
                                            >
                                              {type === "fine"
                                                ? `${typeTotal.toFixed(2)}g`
                                                : `₹${typeTotal.toLocaleString(
                                                    "en-IN"
                                                  )}`}
                                            </Typography>
                                          </Box>
                                        </Box>

                                        {/* Items Table for this type */}
                                        <Box
                                          sx={{
                                            borderRadius: "12px",
                                            overflow: "hidden",
                                            border: `1px solid ${alpha(
                                              isDark ? "#475569" : "#e2e8f0",
                                              isDark ? 0.2 : 0.3
                                            )}`,
                                            background: isDark
                                              ? "rgba(15, 23, 42, 0.5)"
                                              : "rgba(255, 255, 255, 0.7)",
                                            backdropFilter: "blur(10px)",
                                          }}
                                        >
                                          <TableContainer
                                            sx={{ maxHeight: 300 }}
                                          >
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell
                                                    sx={{
                                                      backgroundColor: isDark
                                                        ? "rgba(30, 41, 59, 0.9)"
                                                        : "rgba(248, 250, 252, 0.95)",
                                                      color: isDark
                                                        ? "#94a3b8"
                                                        : "#64748b",
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                      borderBottom: `2px solid ${alpha(
                                                        isDark
                                                          ? "#475569"
                                                          : "#e2e8f0",
                                                        isDark ? 0.3 : 0.4
                                                      )}`,
                                                    }}
                                                  >
                                                    #
                                                  </TableCell>
                                                  <TableCell
                                                    sx={{
                                                      backgroundColor: isDark
                                                        ? "rgba(30, 41, 59, 0.9)"
                                                        : "rgba(248, 250, 252, 0.95)",
                                                      color: isDark
                                                        ? "#94a3b8"
                                                        : "#64748b",
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                      borderBottom: `2px solid ${alpha(
                                                        isDark
                                                          ? "#475569"
                                                          : "#e2e8f0",
                                                        isDark ? 0.3 : 0.4
                                                      )}`,
                                                    }}
                                                  >
                                                    Item
                                                  </TableCell>
                                                  <TableCell
                                                    sx={{
                                                      backgroundColor: isDark
                                                        ? "rgba(30, 41, 59, 0.9)"
                                                        : "rgba(248, 250, 252, 0.95)",
                                                      color: isDark
                                                        ? "#94a3b8"
                                                        : "#64748b",
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                      borderBottom: `2px solid ${alpha(
                                                        isDark
                                                          ? "#475569"
                                                          : "#e2e8f0",
                                                        isDark ? 0.3 : 0.4
                                                      )}`,
                                                    }}
                                                  >
                                                    Metal
                                                  </TableCell>

                                                  {type === "piece" && (
                                                    <>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Pieces
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Price/PC
                                                      </TableCell>
                                                    </>
                                                  )}

                                                  {type === "melting" && (
                                                    <>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Melting %
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Wastage %
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Rate/g
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Weight
                                                      </TableCell>
                                                    </>
                                                  )}

                                                  {type === "pure" && (
                                                    <>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Rate/g
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Labour/g
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Weight
                                                      </TableCell>
                                                    </>
                                                  )}

                                                  {type === "fine" && (
                                                    <>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Weight
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Melting %
                                                      </TableCell>
                                                      <TableCell
                                                        align="right"
                                                        sx={{
                                                          backgroundColor:
                                                            isDark
                                                              ? "rgba(30, 41, 59, 0.9)"
                                                              : "rgba(248, 250, 252, 0.95)",
                                                          color: isDark
                                                            ? "#94a3b8"
                                                            : "#64748b",
                                                          fontWeight: 700,
                                                          fontSize: "0.75rem",
                                                          borderBottom: `2px solid ${alpha(
                                                            isDark
                                                              ? "#475569"
                                                              : "#e2e8f0",
                                                            isDark ? 0.3 : 0.4
                                                          )}`,
                                                        }}
                                                      >
                                                        Wastage %
                                                      </TableCell>
                                                    </>
                                                  )}

                                                  <TableCell
                                                    align="right"
                                                    sx={{
                                                      backgroundColor: isDark
                                                        ? "rgba(30, 41, 59, 0.9)"
                                                        : "rgba(248, 250, 252, 0.95)",
                                                      color: isDark
                                                        ? "#94a3b8"
                                                        : "#64748b",
                                                      fontWeight: 700,
                                                      fontSize: "0.75rem",
                                                      borderBottom: `2px solid ${alpha(
                                                        isDark
                                                          ? "#475569"
                                                          : "#e2e8f0",
                                                        isDark ? 0.3 : 0.4
                                                      )}`,
                                                    }}
                                                  >
                                                    Amount
                                                  </TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {typeItems.map(
                                                  (item, index) => {
                                                    const itemTotal =
                                                      calculateItemTotal(item);
                                                    // const isEven = index % 2 === 0;

                                                    return (
                                                      <TableRow
                                                        key={index}
                                                        hover
                                                        sx={{
                                                          "&:hover": {
                                                            backgroundColor:
                                                              isDark
                                                                ? "rgba(255, 255, 255, 0.05)"
                                                                : "rgba(0, 0, 0, 0.02)",
                                                            transform:
                                                              "translateY(-2px)",
                                                            transition:
                                                              "all 0.2s ease",
                                                          },
                                                          transition:
                                                            "all 0.2s ease",
                                                          position: "relative",
                                                          "&::after": {
                                                            content: '""',
                                                            position:
                                                              "absolute",
                                                            bottom: 0,
                                                            left: 0,
                                                            right: 0,
                                                            height: 1,
                                                            background: isDark
                                                              ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)"
                                                              : "linear-gradient(90deg, transparent, rgba(0,0,0,0.03), transparent)",
                                                          },
                                                        }}
                                                      >
                                                        <TableCell>
                                                          <Box
                                                            sx={{
                                                              width: 28,
                                                              height: 28,
                                                              borderRadius:
                                                                "8px",
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              justifyContent:
                                                                "center",
                                                              background: alpha(
                                                                "#3b82f6",
                                                                0.1
                                                              ),
                                                              color: "#3b82f6",
                                                              fontWeight: 700,
                                                              fontSize:
                                                                "0.875rem",
                                                            }}
                                                          >
                                                            {index + 1}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                          <Box>
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontWeight: 600,
                                                                color: isDark
                                                                  ? "#f1f5f9"
                                                                  : "#1e293b",
                                                              }}
                                                            >
                                                              {item.itemName ||
                                                                "Unnamed Item"}
                                                            </Typography>
                                                            {item.description && (
                                                              <Typography
                                                                variant="caption"
                                                                sx={{
                                                                  color: isDark
                                                                    ? "#94a3b8"
                                                                    : "#64748b",
                                                                  display:
                                                                    "block",
                                                                  fontSize:
                                                                    "0.7rem",
                                                                }}
                                                              >
                                                                {
                                                                  item.description
                                                                }
                                                              </Typography>
                                                            )}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                          <Chip
                                                            label={
                                                              item.metal
                                                                ?.charAt(0)
                                                                .toUpperCase() +
                                                                item.metal?.slice(
                                                                  1
                                                                ) || "N/A"
                                                            }
                                                            size="small"
                                                            sx={{
                                                              fontSize:
                                                                "0.7rem",
                                                              height: 20,
                                                              backgroundColor:
                                                                alpha(
                                                                  "#3b82f6",
                                                                  0.1
                                                                ),
                                                              color: "#3b82f6",
                                                              border: `1px solid ${alpha(
                                                                "#3b82f6",
                                                                0.2
                                                              )}`,
                                                            }}
                                                          />
                                                        </TableCell>

                                                        {/* Type-specific columns */}
                                                        {type === "piece" && (
                                                          <>
                                                            <TableCell align="right">
                                                              <Box
                                                                sx={{
                                                                  display:
                                                                    "inline-flex",
                                                                  alignItems:
                                                                    "center",
                                                                  gap: 0.5,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#3b82f6",
                                                                      0.1
                                                                    ),
                                                                  padding:
                                                                    "2px 8px",
                                                                  borderRadius: 1,
                                                                  border: `1px solid ${alpha(
                                                                    "#3b82f6",
                                                                    0.2
                                                                  )}`,
                                                                }}
                                                              >
                                                                <NumbersIcon
                                                                  sx={{
                                                                    fontSize: 12,
                                                                    color:
                                                                      "#3b82f6",
                                                                  }}
                                                                />
                                                                <Typography
                                                                  variant="body2"
                                                                  sx={{
                                                                    fontWeight: 700,
                                                                    color:
                                                                      "#3b82f6",
                                                                  }}
                                                                >
                                                                  {item.count ||
                                                                    0}
                                                                </Typography>
                                                              </Box>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontWeight: 600,
                                                                  color:
                                                                    "text.primary",
                                                                }}
                                                              >
                                                                ₹
                                                                {parseFloat(
                                                                  item.price ||
                                                                    0
                                                                ).toLocaleString(
                                                                  "en-IN"
                                                                )}
                                                              </Typography>
                                                            </TableCell>
                                                          </>
                                                        )}

                                                        {type === "melting" && (
                                                          <>
                                                            <TableCell align="right">
                                                              <Chip
                                                                label={`${
                                                                  item.percent ||
                                                                  0
                                                                }%`}
                                                                size="small"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.7rem",
                                                                  height: 20,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#f59e0b",
                                                                      0.1
                                                                    ),
                                                                  color:
                                                                    "#f59e0b",
                                                                  border: `1px solid ${alpha(
                                                                    "#f59e0b",
                                                                    0.2
                                                                  )}`,
                                                                  fontWeight: 700,
                                                                }}
                                                              />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Chip
                                                                label={`${
                                                                  item.wastage ||
                                                                  0
                                                                }%`}
                                                                size="small"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.7rem",
                                                                  height: 20,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#ef4444",
                                                                      0.1
                                                                    ),
                                                                  color:
                                                                    "#ef4444",
                                                                  border: `1px solid ${alpha(
                                                                    "#ef4444",
                                                                    0.2
                                                                  )}`,
                                                                  fontWeight: 700,
                                                                }}
                                                              />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontWeight: 600,
                                                                  color:
                                                                    "text.primary",
                                                                }}
                                                              >
                                                                ₹
                                                                {parseFloat(
                                                                  item.rate || 0
                                                                ).toLocaleString(
                                                                  "en-IN"
                                                                )}
                                                              </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Chip
                                                                label={`${parseFloat(
                                                                  item.weight ||
                                                                    0
                                                                ).toFixed(2)}g`}
                                                                size="small"
                                                                icon={
                                                                  <ScaleIcon
                                                                    sx={{
                                                                      fontSize: 12,
                                                                    }}
                                                                  />
                                                                }
                                                                sx={{
                                                                  fontSize:
                                                                    "0.7rem",
                                                                  height: 20,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#3b82f6",
                                                                      0.1
                                                                    ),
                                                                  color:
                                                                    "#3b82f6",
                                                                  border: `1px solid ${alpha(
                                                                    "#3b82f6",
                                                                    0.2
                                                                  )}`,
                                                                  fontWeight: 700,
                                                                }}
                                                              />
                                                            </TableCell>
                                                          </>
                                                        )}

                                                        {type === "pure" && (
                                                          <>
                                                            <TableCell align="right">
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontWeight: 600,
                                                                  color:
                                                                    "text.primary",
                                                                }}
                                                              >
                                                                ₹
                                                                {parseFloat(
                                                                  item.rate || 0
                                                                ).toLocaleString(
                                                                  "en-IN"
                                                                )}
                                                              </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Typography
                                                                variant="body2"
                                                                sx={{
                                                                  fontWeight: 600,
                                                                  color:
                                                                    "text.primary",
                                                                }}
                                                              >
                                                                ₹
                                                                {parseFloat(
                                                                  item.labour ||
                                                                    0
                                                                ).toLocaleString(
                                                                  "en-IN"
                                                                )}
                                                              </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Chip
                                                                label={`${parseFloat(
                                                                  item.weight ||
                                                                    0
                                                                ).toFixed(2)}g`}
                                                                size="small"
                                                                icon={
                                                                  <ScaleIcon
                                                                    sx={{
                                                                      fontSize: 12,
                                                                    }}
                                                                  />
                                                                }
                                                                sx={{
                                                                  fontSize:
                                                                    "0.7rem",
                                                                  height: 20,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#3b82f6",
                                                                      0.1
                                                                    ),
                                                                  color:
                                                                    "#3b82f6",
                                                                  border: `1px solid ${alpha(
                                                                    "#3b82f6",
                                                                    0.2
                                                                  )}`,
                                                                  fontWeight: 700,
                                                                }}
                                                              />
                                                            </TableCell>
                                                          </>
                                                        )}

                                                        {type === "fine" && (
                                                          <>
                                                            <TableCell align="right">
                                                              <Chip
                                                                label={`${parseFloat(
                                                                  item.weight ||
                                                                    0
                                                                ).toFixed(2)}g`}
                                                                size="small"
                                                                icon={
                                                                  <ScaleIcon
                                                                    sx={{
                                                                      fontSize: 12,
                                                                    }}
                                                                  />
                                                                }
                                                                sx={{
                                                                  fontSize:
                                                                    "0.7rem",
                                                                  height: 20,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#3b82f6",
                                                                      0.1
                                                                    ),
                                                                  color:
                                                                    "#3b82f6",
                                                                  border: `1px solid ${alpha(
                                                                    "#3b82f6",
                                                                    0.2
                                                                  )}`,
                                                                  fontWeight: 700,
                                                                }}
                                                              />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Chip
                                                                label={`${
                                                                  item.melting ||
                                                                  0
                                                                }%`}
                                                                size="small"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.7rem",
                                                                  height: 20,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#f59e0b",
                                                                      0.1
                                                                    ),
                                                                  color:
                                                                    "#f59e0b",
                                                                  border: `1px solid ${alpha(
                                                                    "#f59e0b",
                                                                    0.2
                                                                  )}`,
                                                                  fontWeight: 700,
                                                                }}
                                                              />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                              <Chip
                                                                label={`${
                                                                  item.wastage ||
                                                                  0
                                                                }%`}
                                                                size="small"
                                                                sx={{
                                                                  fontSize:
                                                                    "0.7rem",
                                                                  height: 20,
                                                                  backgroundColor:
                                                                    alpha(
                                                                      "#ef4444",
                                                                      0.1
                                                                    ),
                                                                  color:
                                                                    "#ef4444",
                                                                  border: `1px solid ${alpha(
                                                                    "#ef4444",
                                                                    0.2
                                                                  )}`,
                                                                  fontWeight: 700,
                                                                }}
                                                              />
                                                            </TableCell>
                                                          </>
                                                        )}

                                                        <TableCell align="right">
                                                          <Box
                                                            sx={{
                                                              display:
                                                                "inline-flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 0.5,
                                                              backgroundColor:
                                                                alpha(
                                                                  "#10b981",
                                                                  0.1
                                                                ),
                                                              padding:
                                                                "2px 8px",
                                                              borderRadius:
                                                                "6px",
                                                              border: `1px solid ${alpha(
                                                                "#10b981",
                                                                0.2
                                                              )}`,
                                                            }}
                                                          >
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontWeight: 700,
                                                                color:
                                                                  "#10b981",
                                                              }}
                                                            >
                                                              {type === "fine"
                                                                ? `${itemTotal.toFixed(
                                                                    2
                                                                  )}g`
                                                                : `₹${itemTotal.toLocaleString(
                                                                    "en-IN"
                                                                  )}`}
                                                            </Typography>
                                                          </Box>
                                                        </TableCell>
                                                      </TableRow>
                                                    );
                                                  }
                                                )}
                                              </TableBody>
                                            </Table>
                                          </TableContainer>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              );
                            })()}
                          </Paper>
                        </Grid>

                        {/* Right Column - Financial Summary */}
                        <Grid item xs={12} lg={5}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 2,
                            }}
                          >
                            {/* Payment Summary Card */}
                            <Paper
                              sx={{
                                p: 3,
                                borderRadius: "20px",
                                background: isDark
                                  ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)"
                                  : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)",
                                backdropFilter: "blur(20px)",
                                border: `1px solid ${alpha(
                                  isDark ? "#475569" : "#e2e8f0",
                                  isDark ? 0.2 : 0.3
                                )}`,
                                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 3,
                                  fontWeight: 800,
                                  color: isDark ? "#f1f5f9" : "#1e293b",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background:
                                      "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                                    boxShadow:
                                      "0 4px 20px rgba(59, 130, 246, 0.3)",
                                  }}
                                >
                                  <PaymentIcon
                                    sx={{ fontSize: 20, color: "white" }}
                                  />
                                </Box>
                                Payment Summary
                              </Typography>

                              <Stack spacing={2}>
                                {/* Cash Payment */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: "10px",
                                    background: isDark
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${alpha(
                                      isDark ? "#475569" : "#e2e8f0",
                                      isDark ? 0.1 : 0.2
                                    )}`,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      background: isDark
                                        ? "rgba(255, 255, 255, 0.08)"
                                        : "rgba(0, 0, 0, 0.04)",
                                      transform: "translateX(4px)",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: alpha("#3b82f6", 0.1),
                                        border: `1px solid ${alpha(
                                          "#3b82f6",
                                          0.2
                                        )}`,
                                      }}
                                    >
                                      <LocalAtmIcon
                                        sx={{ fontSize: 16, color: "#3b82f6" }}
                                      />
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: isDark ? "#f1f5f9" : "#1e293b",
                                      }}
                                    >
                                      Cash Payment
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#10b981",
                                      fontSize: "1rem",
                                    }}
                                  >
                                    ₹
                                    {(
                                      selectedTransaction.payments?.cash || 0
                                    ).toLocaleString("en-IN")}
                                  </Typography>
                                </Box>

                                {/* Bank Transfer */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: "10px",
                                    background: isDark
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "rgba(0, 0, 0, 0.02)",
                                    border: `1px solid ${alpha(
                                      isDark ? "#475569" : "#e2e8f0",
                                      isDark ? 0.1 : 0.2
                                    )}`,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      background: isDark
                                        ? "rgba(255, 255, 255, 0.08)"
                                        : "rgba(0, 0, 0, 0.04)",
                                      transform: "translateX(4px)",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: alpha("#3b82f6", 0.1),
                                        border: `1px solid ${alpha(
                                          "#3b82f6",
                                          0.2
                                        )}`,
                                      }}
                                    >
                                      <AccountBalanceIcon
                                        sx={{ fontSize: 16, color: "#3b82f6" }}
                                      />
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: isDark ? "#f1f5f9" : "#1e293b",
                                      }}
                                    >
                                      Bank Transfer
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#10b981",
                                      fontSize: "1rem",
                                    }}
                                  >
                                    ₹
                                    {(
                                      selectedTransaction.payments?.bank || 0
                                    ).toLocaleString("en-IN")}
                                  </Typography>
                                </Box>

                                {/* Total Payment Done */}
                                <Box
                                  sx={{
                                    mt: 2,
                                    pt: 2,
                                    borderTop: `1px solid ${alpha(
                                      "#10b981",
                                      0.3
                                    )}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      py: 1.5,
                                      px: 2,
                                      borderRadius: "10px",
                                      background:
                                        "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)",
                                      border: `1px solid ${alpha(
                                        "#10b981",
                                        0.3
                                      )}`,
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{ fontWeight: 700, color: "#10b981" }}
                                    >
                                      Total Payment Done
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 800,
                                        color: "#10b981",
                                      }}
                                    >
                                      ₹
                                      {(
                                        selectedTransaction.summary
                                          ?.paymentDone || 0
                                      ).toLocaleString("en-IN")}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Stack>
                            </Paper>

                            {/* Totals Summary Card */}
                            <Paper
                              sx={{
                                p: 3,
                                borderRadius: "20px",
                                background: isDark
                                  ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)"
                                  : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)",
                                backdropFilter: "blur(20px)",
                                border: `1px solid ${alpha(
                                  isDark ? "#475569" : "#e2e8f0",
                                  isDark ? 0.2 : 0.3
                                )}`,
                                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  mb: 3,
                                  fontWeight: 800,
                                  color: isDark ? "#f1f5f9" : "#1e293b",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "12px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background:
                                      "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
                                    boxShadow:
                                      "0 4px 20px rgba(139, 92, 246, 0.3)",
                                  }}
                                >
                                  <TrendingUpIcon
                                    sx={{ fontSize: 20, color: "white" }}
                                  />
                                </Box>
                                Financial Summary
                              </Typography>

                              <Stack spacing={2}>
                                {/* Subtotal */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1.5,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#94a3b8" : "#64748b",
                                    }}
                                  >
                                    Subtotal
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    ₹
                                    {(
                                      selectedTransaction.summary?.subTotal || 0
                                    ).toFixed(2)}
                                  </Typography>
                                </Box>

                                {/* GST if applicable */}
                                {selectedTransaction.gstApplicable && (
                                  <>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        py: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isDark ? "#94a3b8" : "#64748b",
                                        }}
                                      >
                                        CGST (9%)
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#ef4444",
                                        }}
                                      >
                                        ₹
                                        {(
                                          selectedTransaction.summary?.cgst || 0
                                        ).toFixed(2)}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        py: 1.5,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: isDark ? "#94a3b8" : "#64748b",
                                        }}
                                      >
                                        SGST (9%)
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#ef4444",
                                        }}
                                      >
                                        ₹
                                        {(
                                          selectedTransaction.summary?.sgst || 0
                                        ).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </>
                                )}

                                {/* Discount */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: "10px",
                                    background:
                                      "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)",
                                    border: `1px solid ${alpha(
                                      "#10b981",
                                      0.2
                                    )}`,
                                    mt: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: alpha("#10b981", 0.15),
                                        border: `1px solid ${alpha(
                                          "#10b981",
                                          0.3
                                        )}`,
                                      }}
                                    >
                                      <DiscountIcon
                                        sx={{ fontSize: 16, color: "#10b981" }}
                                      />
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, color: "#10b981" }}
                                    >
                                      Discount
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 700,
                                      color: "#10b981",
                                      fontSize: "1rem",
                                    }}
                                  >
                                    -₹
                                    {(
                                      selectedTransaction.summary?.discount || 0
                                    ).toFixed(2)}
                                  </Typography>
                                </Box>

                                {/* Divider */}
                                <Divider
                                  sx={{
                                    borderColor: isDark ? "#475569" : "#e2e8f0",
                                  }}
                                />

                                {/* Grand Total - Highlighted */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 2,
                                    px: 2,
                                    borderRadius: "12px",
                                    background:
                                      "linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)",
                                    border: `1px solid ${alpha(
                                      "#2563eb",
                                      0.2
                                    )}`,
                                    mt: 1,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      color: isDark ? "#f1f5f9" : "#1e293b",
                                    }}
                                  >
                                    Grand Total
                                  </Typography>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      fontWeight: 800,
                                      background:
                                        "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                      backgroundClip: "text",
                                    }}
                                  >
                                    ₹
                                    {(
                                      selectedTransaction.summary?.grandTotal ||
                                      0
                                    ).toFixed(2)}
                                  </Typography>
                                </Box>

                                {/* Pending Amount */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: "10px",
                                    background:
                                      selectedTransaction.summary
                                        ?.pendingAmount > 0
                                        ? "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)"
                                        : "linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)",
                                    border: `1px solid ${alpha(
                                      selectedTransaction.summary
                                        ?.pendingAmount > 0
                                        ? "#ef4444"
                                        : "#10b981",
                                      0.2
                                    )}`,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {selectedTransaction.summary
                                      ?.pendingAmount > 0 ? (
                                      <ErrorOutlineIcon
                                        sx={{ fontSize: 16, color: "#ef4444" }}
                                      />
                                    ) : (
                                      <CheckCircleOutlineIcon
                                        sx={{ fontSize: 16, color: "#10b981" }}
                                      />
                                    )}
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 700,
                                        color:
                                          selectedTransaction.summary
                                            ?.pendingAmount > 0
                                            ? "#ef4444"
                                            : "#10b981",
                                      }}
                                    >
                                      {selectedTransaction.summary
                                        ?.pendingAmount > 0
                                        ? "Pending Amount"
                                        : "Payment Status"}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color:
                                        selectedTransaction.summary
                                          ?.pendingAmount > 0
                                          ? "#ef4444"
                                          : "#10b981",
                                    }}
                                  >
                                    {selectedTransaction.summary
                                      ?.pendingAmount > 0
                                      ? "₹" +
                                        (
                                          selectedTransaction.summary
                                            .pendingAmount || 0
                                        ).toFixed(2)
                                      : "Fully Paid"}
                                  </Typography>
                                </Box>

                                {/* Payment Progress */}
                                {selectedTransaction.summary?.pendingAmount >
                                  0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: isDark ? "#94a3b8" : "#64748b",
                                        mb: 1,
                                        display: "block",
                                      }}
                                    >
                                      Payment Progress
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={
                                        ((selectedTransaction.summary
                                          ?.grandTotal -
                                          selectedTransaction.summary
                                            ?.pendingAmount) /
                                          selectedTransaction.summary
                                            ?.grandTotal) *
                                        100
                                      }
                                      sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: isDark
                                          ? "#374151"
                                          : "#e5e7eb",
                                        "& .MuiLinearProgress-bar": {
                                          background:
                                            "linear-gradient(90deg, #10b981, #34d399)",
                                          borderRadius: 4,
                                        },
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        mt: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#10b981" }}
                                      >
                                        {(
                                          ((selectedTransaction.summary
                                            ?.grandTotal -
                                            selectedTransaction.summary
                                              ?.pendingAmount) /
                                            selectedTransaction.summary
                                              ?.grandTotal) *
                                          100
                                        ).toFixed(1)}
                                        % Paid
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#ef4444" }}
                                      >
                                        {(
                                          (selectedTransaction.summary
                                            ?.pendingAmount /
                                            selectedTransaction.summary
                                              ?.grandTotal) *
                                          100
                                        ).toFixed(1)}
                                        % Pending
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Stack>
                            </Paper>

                            {/* Fine Details Card */}
                            {selectedTransaction.finePayments &&
                              selectedTransaction.finePayments.length > 0 && (
                                <Paper
                                  sx={{
                                    p: 3,
                                    borderRadius: "20px",
                                    background: isDark
                                      ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)"
                                      : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)",
                                    backdropFilter: "blur(20px)",
                                    border: `1px solid ${alpha(
                                      isDark ? "#475569" : "#e2e8f0",
                                      isDark ? 0.2 : 0.3
                                    )}`,
                                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                                    maxWidth: "225px",
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      mb: 3,
                                      fontWeight: 800,
                                      color: isDark ? "#f1f5f9" : "#1e293b",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "12px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background:
                                          "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                                        boxShadow:
                                          "0 4px 20px rgba(245, 158, 11, 0.3)",
                                      }}
                                    >
                                      <ScaleIcon
                                        sx={{ fontSize: 20, color: "white" }}
                                      />
                                    </Box>
                                    Fine Details
                                  </Typography>

                                  <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          py: 1.5,
                                          px: 2,
                                          borderRadius: "10px",
                                          background: isDark
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                          border: `1px solid ${alpha(
                                            isDark ? "#475569" : "#e2e8f0",
                                            isDark ? 0.1 : 0.2
                                          )}`,
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            background: isDark
                                              ? "rgba(255, 255, 255, 0.08)"
                                              : "rgba(0, 0, 0, 0.04)",
                                            transform: "translateX(4px)",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: isDark
                                                ? "#f1f5f9"
                                                : "#1e293b",
                                            }}
                                          >
                                            Fine Balance
                                          </Typography>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: 800,
                                              color: "#f59e0b",
                                            }}
                                          >
                                            {(
                                              selectedTransaction.summary
                                                ?.fineBalance || 0
                                            ).toFixed(2)}
                                            g
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          py: 1.5,
                                          px: 2,
                                          borderRadius: "10px",
                                          background: isDark
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                          border: `1px solid ${alpha(
                                            isDark ? "#475569" : "#e2e8f0",
                                            isDark ? 0.1 : 0.2
                                          )}`,
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            background: isDark
                                              ? "rgba(255, 255, 255, 0.08)"
                                              : "rgba(0, 0, 0, 0.04)",
                                            transform: "translateX(4px)",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: isDark
                                                ? "#f1f5f9"
                                                : "#1e293b",
                                            }}
                                          >
                                            Fine Payment
                                          </Typography>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: 800,
                                              color: "#10b981",
                                            }}
                                          >
                                            {(
                                              selectedTransaction.summary
                                                ?.finePayment || 0
                                            ).toFixed(2)}
                                            g
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          py: 1.5,
                                          px: 2,
                                          borderRadius: "10px",
                                          background: isDark
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                          border: `1px solid ${alpha(
                                            isDark ? "#475569" : "#e2e8f0",
                                            isDark ? 0.1 : 0.2
                                          )}`,
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            background: isDark
                                              ? "rgba(255, 255, 255, 0.08)"
                                              : "rgba(0, 0, 0, 0.04)",
                                            transform: "translateX(4px)",
                                          },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: isDark
                                                ? "#f1f5f9"
                                                : "#1e293b",
                                            }}
                                          >
                                            Pending Fine
                                          </Typography>
                                          <Typography
                                            variant="h6"
                                            sx={{
                                              fontWeight: 800,
                                              color: "#ef4444",
                                            }}
                                          >
                                            {(
                                              selectedTransaction.summary
                                                ?.pendingFine || 0
                                            ).toFixed(2)}
                                            g
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                )}
              </Dialog>
            </Container>
          </Box>
        </LocalizationProvider>
      )}
    </>
  );
};

// Helper functions for bill generation
const generateSupplierBillHTML = (transaction, supplier) => {
  const isGst = transaction.gstApplicable;

  const calculateItemAmountForBill = (item) => {
    if (item.type === "piece") {
      return (parseFloat(item.count) || 0) * (parseFloat(item.price) || 0);
    } else if (item.type === "melting") {
      const percent = parseFloat(item.percent) || 0;
      const rate = parseFloat(item.rate) || 0;
      const weight = parseFloat(item.weight) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      return ((wastage + percent) / 100) * weight * rate;
    } else if (item.type === "pure") {
      const rate = parseFloat(item.rate) || 0;
      const weight = parseFloat(item.weight) || 0;
      const labour = parseFloat(item.labour) || 0;
      return weight * (rate + labour);
    } else if (item.type === "fine") {
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      const weight = parseFloat(item.weight) || 0;
      return ((melting + wastage) / 100) * weight;
    }
    return 0;
  };

  return `
    <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; border: 1px solid #ddd;">
      <div style="text-align: center; margin-bottom: 20px;">
        <h1 style="color: #2563EB;">${
          isGst ? "SUPPLIER GST INVOICE" : "SUPPLIER PURCHASE RECEIPT"
        }</h1>
        <h3>Jewellery Shop</h3>
      </div>
      
      <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
        <div>
          <h4>Supplier Details:</h4>
          <p><strong>Name:</strong> ${supplier?.name || "N/A"}</p>
          <p><strong>Contact:</strong> ${supplier?.contact || "N/A"}</p>
          <p><strong>Address:</strong> ${supplier?.address || "N/A"}</p>
        </div>
        <div>
          <h4>Transaction Details:</h4>
          <p><strong>Date:</strong> ${formatDateSafely(transaction.date)}</p>
          <p><strong>GST:</strong> ${
            isGst ? "Applicable" : "Not Applicable"
          }</p>
        </div>
      </div>
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
        <thead>
          <tr style="background-color: #f3f4f6;">
            <th style="border: 1px solid #ddd; padding: 8px;">#</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Item Name</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Type</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Metal</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Weight</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Rate</th>
            <th style="border: 1px solid #ddd; padding: 8px;">Amount</th>
           </tr>
        </thead>
        <tbody>
          ${transaction.items
            ?.map((item, index) => {
              const amount = calculateItemAmountForBill(item);
              return `
               <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  index + 1
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.itemName || "N/A"
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.type || "N/A"
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  item.metal || "N/A"
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${
                  item.weight || 0
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${
                  item.rate || 0
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">₹${amount.toFixed(
                  2
                )}</td>
               </tr>
            `;
            })
            .join("")}
        </tbody>
      </table>
      
      <div style="display: flex; justify-content: space-between;">
        <div style="width: 45%;">
          <h4>Payment Details:</h4>
          <p><strong>Cash:</strong> ₹${transaction.payments?.cash || 0}</p>
          <p><strong>Bank:</strong> ₹${transaction.payments?.bank || 0}</p>
          <p><strong>Total Payment:</strong> ₹${
            (transaction.payments?.cash || 0) +
            (transaction.payments?.bank || 0)
          }</p>
        </div>
        <div style="width: 45%;">
          <h4>Summary:</h4>
          <p><strong>Subtotal:</strong> ₹${
            transaction.summary?.subTotal?.toFixed(2) || 0
          }</p>
          ${
            isGst
              ? `
            <p><strong>CGST (1.5%):</strong> ₹${
              transaction.summary?.cgst?.toFixed(2) || 0
            }</p>
            <p><strong>SGST (1.5%):</strong> ₹${
              transaction.summary?.sgst?.toFixed(2) || 0
            }</p>
          `
              : ""
          }
          <p><strong>Discount:</strong> ₹${
            transaction.summary?.discount?.toFixed(2) || 0
          }</p>
          <p><strong>Grand Total:</strong> ₹${
            transaction.summary?.grandTotal?.toFixed(2) || 0
          }</p>
          <p><strong>Paid:</strong> ₹${
            transaction.summary?.paymentDone?.toFixed(2) || 0
          }</p>
          <p><strong>Pending:</strong> ₹${
            transaction.summary?.pendingAmount?.toFixed(2) || 0
          }</p>
        </div>
      </div>
      
      <div style="margin-top: 20px; padding-top: 20px; border-top: 2px solid #2563EB;">
        <h4>Fine Details:</h4>
        <p><strong>Fine Balance:</strong> ${
          transaction.summary?.fineBalance?.toFixed(2) || 0
        }g</p>
        <p><strong>Fine Payment:</strong> ${
          transaction.summary?.finePayment?.toFixed(2) || 0
        }g</p>
        <p><strong>Pending Fine:</strong> ${
          transaction.summary?.pendingFine?.toFixed(2) || 0
        }g</p>
      </div>
      
      <div style="margin-top: 30px; text-align: center; color: #666;">
        <p>Thank you for your business!</p>
        <p>Signature: ________________________</p>
      </div>
    </div>
  `;
};

export default React.memo(SupplierTransactionHistory);
